/* What every dashboard widget reads: the person's Accounts and access, the page's
   Account pick and money mode, and the shared Statistics requests. */

import { createContext, useContext, useEffect } from 'react';
import type { Account, AccountSubscriptionStatus, ChatStatsResponse, StatisticsResponse } from '../api/client';
import { shiftDays, type MoneyMode } from '../components/Statistics/unified/model';
import { PERIOD_DAYS, type Access, type Period } from './model';

export type Source = 'stats' | 'chat';
export interface SourceData {
  stats: StatisticsResponse;
  chat: ChatStatsResponse;
}
export interface Range {
  start: string;
  end: string;
}

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  /** The person may not see Statistics for this scope (403). */
  noAccess: boolean;
  loading: boolean;
  reload: () => void;
}

export interface SourceEntry {
  data: unknown;
  error: string | null;
  noAccess: boolean;
  loading: boolean;
}

export interface SourceCache {
  get: (key: string) => SourceEntry | undefined;
  /** A widget starts showing this range; pair every call with `release`. */
  ensure: (key: string, source: Source, range: Range) => void;
  /** A widget stops showing this range; the last one out drops it. */
  release: (key: string) => void;
  reload: (key: string) => void;
  /** Bumped whenever an entry changes, so readers re-render. */
  version: number;
}

export interface DashEnv {
  /** Accounts in view: all of the person's, or the one picked. */
  accounts: Account[];
  subs: Record<string, AccountSubscriptionStatus>;
  throttled: ReadonlySet<string>;
  /** The Account picked in the switcher; undefined = All. */
  accountId?: string;
  money: MoneyMode;
  access: Access;
  /** Money covers this many of the Accounts in view; null while not known. */
  statsScope: { n: number; of: number } | null;
  hasStatsOn: (accountId: string) => boolean;
  canToggle: (accountId: string) => boolean;
  editing: boolean;
  today: string;
  openAccount: (account: Account) => void;
  reconnect: (account: Account) => void;
  setEnabled: (accounts: Account[], enabled: boolean) => Promise<{ ok: number; failed: number }>;
  snoozes: Record<string, string>;
  setSnoozed: (keys: string[], on: boolean) => void;
  cache: SourceCache;
}

export const DashContext = createContext<DashEnv | null>(null);

export function useDash(): DashEnv {
  const env = useContext(DashContext);
  if (!env) throw new Error('useDash must be used inside the Dashboard');
  return env;
}

export function periodRange(per: Period, today: string): Range {
  return { start: shiftDays(today, -(PERIOD_DAYS[per] - 1)), end: today };
}

/** The window of the same length right before `range`. */
export function previousRange(range: Range): Range {
  const days = Math.round((Date.parse(`${range.end}T00:00:00Z`) - Date.parse(`${range.start}T00:00:00Z`)) / 86400000) + 1;
  return { start: shiftDays(range.start, -days), end: shiftDays(range.start, -1) };
}

export const sourceKey = (source: Source, range: Range, accountId?: string) => `${source}|${range.start}|${range.end}|${accountId ?? '*'}`;

/** One Statistics request, shared by every widget asking for the same source, range and Account.
    `range` null = not needed (nothing is fetched). */
export function useSource<S extends Source>(source: S, range: Range | null): Loaded<SourceData[S]> {
  const { cache, accountId } = useDash();
  const key = range ? sourceKey(source, range, accountId) : null;
  const start = range?.start;
  const end = range?.end;
  // Not `cache` itself: it is a new object on every answer, and a release + ensure
  // on each one would drop and re-fetch the range forever.
  const { ensure, release } = cache;
  useEffect(() => {
    if (!key || !start || !end) return;
    ensure(key, source, { start, end });
    return () => release(key);
  }, [ensure, release, key, source, start, end]);
  const entry = key ? cache.get(key) : undefined;
  return {
    data: (entry?.data as SourceData[S] | undefined) ?? null,
    error: entry?.error ?? null,
    noAccess: entry?.noAccess ?? false,
    loading: key ? entry?.loading ?? true : false,
    reload: () => { if (key) cache.reload(key); },
  };
}
